import { APP_BASE_HREF } from '@angular/common';
import { computed, inject, Injectable, signal, Type } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { enableNewRmmPage } from '@components/app-header/app-header.constants';
import { MainMenu } from '@components/app-header/app-header.menu';
import { SidepanelIpWhiteListComponent } from '@components/sidepanel-ip-white-list/sidepanel-ip-white-list.component';
import { SidepanelOnlineAccessComponent } from '@components/sidepanel-online-access/sidepanel-online-access.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { Sidepanel, SidepanelCrudBase, SidepanelService } from 'mbs-ui-kit/sidepanel';
import { DotType, MainMenuSidePanelType, MenuItemType } from 'mbs-ui-shared@components/app-header/app-header.types';
import { SidepanelDownloadsComponent } from 'mbs-ui-shared@components/sidepanel-downloads/sidepanel-downloads.component';
import { ApplicationStateFacade } from 'mbs-ui-shared@facades/application.facade';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { BuildsFacade } from 'mbs-ui-shared@facades/builds.facade';
import { LicensesSignalFacade } from 'mbs-ui-shared@facades/licenses-signal.facade';
import { RmmWarehouseFacade } from 'mbs-ui-shared@facades/rmm-warehouse.facade';
import { PermissionsEnum, UiStorageKey } from 'mbs-ui-shared@models';
import { SidepanelPasswordRecoveryComponent } from 'mbs-ui-shared@modules/password-recovery/components/sidepanel-password-recovery/sidepanel-password-recovery.component';
import { EMPTY, filter, map, Observable, of, Subject, switchMap, take } from 'rxjs';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class MainMenuStateService {
  private appBaseHref = inject(APP_BASE_HREF);
  private menu = inject(MainMenu);
  private auth = inject(AuthFacade);
  private ability = inject(AbilityService);
  private buildsFacade = inject(BuildsFacade);
  private applicationStateFacade = inject(ApplicationStateFacade);
  private sidepanelService = inject(SidepanelService);
  private licensesSignalFacade = inject(LicensesSignalFacade);
  private rmmWarehouseFacade = enableNewRmmPage ? inject(RmmWarehouseFacade) : null;

  public readonly sidepanelOpened = new Subject<void>();

  private currentUser = toSignal(this.auth.currentUser$.pipe(filter(Boolean)));
  private showDot = signal<Partial<Record<DotType, boolean>>>({});
  private createdSidepanels = new Set<{ type: Type<Sidepanel>; removable: boolean }>();

  private rmmTemplates = computed(() => this.rmmWarehouseFacade?.presets().map(({ id, name }) => ({ id, name })) ?? []);

  public readonly overviewMenu = computed<MenuItemType[]>(() => (this.currentUser() ? this.menu.getOverviewMenuItems() : []));

  public readonly mainMenu = computed<MenuItemType[]>(() => {
    const user = this.currentUser();

    if (!user) return [];

    const menu = [
      ...this.menu.getComputersMenuItems(),
      ...this.menu.getBackupMenuItems(user, this.licensesSignalFacade.hasRestoreToCloudLicense()),
      ...this.menu.getM365MenuItems(),
      ...this.menu.getRMMMenuItems(user, this.rmmTemplates()),
      ...this.menu.getTicketsMenuItems(),
      ...this.menu.getRepostingMenuItems(),
      ...this.menu.getOrganizationMenuItems(user, this.openSidePanel.bind(this))
    ];

    menu.forEach((item) => {
      this.applyAppBaseHref(item);

      if (item.children) {
        item.can = item.can && item.children.some((c) => c.can);
        item.children.forEach((child) => this.applyAppBaseHref(child));
      }
    });

    return menu;
  });

  public readonly dot = computed<Partial<Record<DotType, boolean>>>(() => {
    const dots = this.showDot();

    return { ...dots, [DotType.Settings]: !!dots[DotType.Notifications] };
  });

  constructor() {
    this.initStreams();
  }

  private initStreams(): void {
    this.buildsFacade.newVersionsAlerts$
      .pipe(untilDestroyed(this))
      .subscribe(({ showBackupUpdateDot, showRmmUpdateDot, showRAUpdateDot }) => {
        this.showDot.update((dot) => ({ ...dot, [DotType.Downloads]: showBackupUpdateDot || showRmmUpdateDot || showRAUpdateDot }));
      });

    this.applicationStateFacade.selectStorageWhenLoaded$.pipe(untilDestroyed(this)).subscribe((storage) => {
      this.showDot.update((dot) => ({
        ...dot,
        [DotType.Notifications]:
          this.ability.can('read', PermissionsEnum.Notification) && storage?.[UiStorageKey.HideNotificationsMenuBadge] !== 'true'
      }));
    });
  }

  public hideDot(type: DotType): void {
    if (type !== DotType.Notifications || !this.showDot()[type]) return;

    this.showDot.update((dot) => ({ ...dot, [DotType.Notifications]: false }));
    this.applicationStateFacade.updateStorageSetting(UiStorageKey.HideNotificationsMenuBadge, 'true').subscribe();
  }

  public openSidePanel(type: MainMenuSidePanelType): void {
    let sidepanel: Observable<any> = EMPTY;

    switch (type) {
      case MainMenuSidePanelType.IpWhiteList:
        sidepanel = this.handleOpenSidepanel(SidepanelIpWhiteListComponent, false, SidepanelIpWhiteListComponent);
        break;
      case MainMenuSidePanelType.Downloads:
        sidepanel = this.handleOpenSidepanel(SidepanelDownloadsComponent);
        break;
      case MainMenuSidePanelType.OnlineAccess:
        sidepanel = this.handleOpenSidepanel(SidepanelOnlineAccessComponent);
        break;
      case MainMenuSidePanelType.PasswordRecovery:
        sidepanel = this.handleOpenSidepanel(SidepanelPasswordRecoveryComponent);
        break;
      default:
        break;
    }

    sidepanel?.subscribe();
  }

  public handleNavigation(): void {
    this.sidepanelService.remove('global_downloads');

    this.createdSidepanels.forEach((s) => {
      if (s.removable || !this.sidepanelService.isOpened(s.type)) this.sidepanelService.removeByType(s.type);
    });
    this.createdSidepanels = new Set();
  }

  private handleOpenSidepanel<T extends Sidepanel>(
    type: Type<T>,
    removable = true,
    toggleLoading?: Type<SidepanelCrudBase<any>>
  ): Observable<T> {
    if (!this.currentUser()?.IsWizardComplete) {
      return of<T>(null);
    }

    return this.sidepanelService.closeAll().pipe(
      take(1),
      switchMap(() => {
        const panel = this.sidepanelService.get(type) || this.sidepanelService.add(type);
        this.createdSidepanels.add({ type, removable });
        this.sidepanelOpened.next();
        toggleLoading && this.sidepanelService.toggleLoadingDataByType(toggleLoading);

        return this.sidepanelService.openByType(type).pipe(map(() => panel));
      }),
      untilDestroyed(this)
    );
  }

  public applyAppBaseHref(item: MenuItemType): MenuItemType {
    if (item.externalUrl?.startsWith('/AP')) {
      item.externalUrl = this.appBaseHref + item.externalUrl;
    }

    return item;
  }
}
